import React, { useState, useRef } from "react";
import { Modal, Form, Button, Alert, Spinner, InputGroup, ListGroup, Nav, Tab } from "react-bootstrap";
import useCreateNewJob from "../../functions/useCreateNewJob";
import useCopyJob from "../../functions/useCopyJob";
import useUserDropdowns from "../../functions/useUserDropdowns";

const CreateJobModal = ({ show, onHide }) => {
  const { createNewJob } = useCreateNewJob();
  const { searchJobs, copyJob, searchResults, isSearching } = useCopyJob();
  
  // Refs for direct form access
  const jobNumberRef = useRef(null);
  const projectNameRef = useRef(null);
  const modelNumberRef = useRef(null);
  const salesRepNameRef = useRef(null);
  const zonesRef = useRef(null);
  const stateRef = useRef(null);
  const sendDocsRef = useRef(null);
  const customControlsRef = useRef(null);
  const newConstructionRef = useRef(null);
  const hasSubmittalRef = useRef(null);

  // Sales Rep dropdown options
  const { salesOptions, loading: dropdownLoading, error: dropdownError } = useUserDropdowns();
  
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [logs, setLogs] = useState([]);
  const [apiStatus, setApiStatus] = useState({});
  const [showApiStatus, setShowApiStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("create");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);

  const handleSearch = async () => {
    if (searchTerm.trim()) {
      await searchJobs(searchTerm);
    }
  };

  const handleSelectJob = (job) => {
    setSelectedJob(job);
    // Pre-fill form fields
    if (jobNumberRef.current) jobNumberRef.current.value = "";
    if (projectNameRef.current) projectNameRef.current.value = `Copy of ${job.ProjectName || job.projectName}`;
    if (modelNumberRef.current) modelNumberRef.current.value = job.ModelNumber || job.modelNumber || "";
    if (salesRepNameRef.current) {
      // Try to match the sales rep by email in dropdown
      const repEmail = job.SalesRepEmail || job.salesRepEmail || job.SalesRepName || job.salesRepName || "";
      salesRepNameRef.current.value = repEmail;
    }
    if (zonesRef.current) zonesRef.current.value = job.Zones || job.zones || "";
    if (stateRef.current) stateRef.current.value = job.State || job.state || "";
  };

  const collectFormData = () => {
    // For salesRepName, get displayName and email from dropdown
    let salesRepValue = salesRepNameRef.current?.value || "";
    let salesRepObj = salesOptions.find(
      (opt) => opt.email === salesRepValue || opt.displayName === salesRepValue
    );
    return {
      jobNumber: jobNumberRef.current?.value || "",
      projectName: projectNameRef.current?.value || "",
      modelNumber: modelNumberRef.current?.value || "",
      salesRepName: salesRepObj ? salesRepObj.displayName : salesRepValue,
      salesRepEmail: salesRepObj ? salesRepObj.email : salesRepValue,
      zones: zonesRef.current?.value || "",
      state: stateRef.current?.value || "",
      sendDocumentsToSales: sendDocsRef.current?.checked || false,
      customControlsEngineering: customControlsRef.current?.checked || false,
      newConstruction: newConstructionRef.current?.checked || false,
      hasSubmittal: hasSubmittalRef.current?.checked || false,
    };
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const currentFormData = collectFormData();
    
    setLogs((prevLogs) => [...prevLogs, "Submitting form data..."]);
    setApiStatus({ generalProject: "loading" });
    setShowApiStatus(true);
    setLoading(true);
    
    try {
      const { jobId, logs: apiLogs, apiStatuses } = await createNewJob(currentFormData, setApiStatus);
      setLogs((prevLogs) => [...prevLogs, ...apiLogs, `Job created successfully with ID: ${jobId}`]);
      setShowSuccessMessage(true);
      resetForm();
      setApiStatus(apiStatuses);
      setTimeout(() => {
        setShowSuccessMessage(false);
        onHide();
        setApiStatus({});
        setLogs([]);
      }, 3000);
    } catch ({ error, logs: apiLogs, apiStatuses }) {
      console.error("Failed to create new job:", error);
      setLogs((prevLogs) => [...prevLogs, ...apiLogs]);
      setApiStatus(apiStatuses);
    } finally {
      setLoading(false);
    }
  };

  const handleCopySubmit = async (e) => {
    e.preventDefault();
    if (!selectedJob) {
      setLogs((prevLogs) => [...prevLogs, "No job selected to copy from."]);
      return;
    }

    const currentFormData = collectFormData();
    
    if (!currentFormData.jobNumber.trim()) {
      setLogs((prevLogs) => [...prevLogs, "Job Number is required."]);
      return;
    }

    setLogs((prevLogs) => [...prevLogs, `Starting to copy job ${selectedJob.JobNumber || selectedJob.jobNumber}...`]);
    setApiStatus({ generalProject: "loading" });
    setShowApiStatus(true);
    setLoading(true);

    try {
      const { jobId, logs: apiLogs, apiStatuses } = await copyJob(
        selectedJob.ID || selectedJob.id,
        currentFormData,
        setApiStatus
      );
      
      setLogs((prevLogs) => [...prevLogs, ...apiLogs, `Job copied successfully with new ID: ${jobId}`]);
      setShowSuccessMessage(true);
      
      resetForm();
      setApiStatus(apiStatuses);
      setTimeout(() => {
        setShowSuccessMessage(false);
        onHide();
        setApiStatus({});
        setLogs([]);
      }, 3000);
    } catch ({ error, logs: apiLogs, apiStatuses }) {
      console.error("Failed to copy job:", error);
      setLogs((prevLogs) => [...prevLogs, ...apiLogs]);
      setApiStatus(apiStatuses);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    // Reset form using refs
    if (jobNumberRef.current) jobNumberRef.current.value = "";
    if (projectNameRef.current) projectNameRef.current.value = "";
    if (modelNumberRef.current) modelNumberRef.current.value = "";
    if (salesRepNameRef.current) salesRepNameRef.current.value = "";
    if (zonesRef.current) zonesRef.current.value = "";
    if (stateRef.current) stateRef.current.value = "";
    if (sendDocsRef.current) sendDocsRef.current.checked = false;
    if (customControlsRef.current) customControlsRef.current.checked = false;
    if (newConstructionRef.current) newConstructionRef.current.checked = false;
    if (hasSubmittalRef.current) hasSubmittalRef.current.checked = false;
    
    setSelectedJob(null);
    setSearchTerm("");
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const renderStatusIcon = (status) => {
    if (status === "loading") {
      return <Spinner animation="border" size="sm" />;
    } else if (status === "success") {
      return <span style={{ color: "green" }}>✔️</span>;
    } else if (status === "failed") {
      return <span style={{ color: "red" }}>❌</span>;
    }
    return null;
  };

  const JobFormFields = ({ required = false }) => (
    <>
      <Form.Group controlId="jobNumber" className="mb-3">
        <Form.Label>Job Number {required && <span className="text-danger">*</span>}</Form.Label>
        <Form.Control
          type="text"
          name="jobNumber"
          ref={jobNumberRef}
          defaultValue=""
          required={required}
          autoComplete="off"
        />
      </Form.Group>
      <Form.Group controlId="projectName" className="mb-3">
        <Form.Label>Project Name {required && <span className="text-danger">*</span>}</Form.Label>
        <Form.Control
          type="text"
          name="projectName"
          ref={projectNameRef}
          defaultValue=""
          required={required}
          autoComplete="off"
        />
      </Form.Group>
      <Form.Group controlId="modelNumber" className="mb-3">
        <Form.Label>Model Number</Form.Label>
        <Form.Control
          type="text"
          name="modelNumber"
          ref={modelNumberRef}
          defaultValue=""
          autoComplete="off"
        />
      </Form.Group>
      <Form.Group controlId="salesRepName" className="mb-3">
        <Form.Label>Sales Rep Name</Form.Label>
        {dropdownLoading ? (
          <div>Loading sales reps...</div>
        ) : dropdownError ? (
          <div className="text-danger">Failed to load sales reps</div>
        ) : (
          <Form.Select
            name="salesRepName"
            ref={salesRepNameRef}
            defaultValue=""
            required={required}
            autoComplete="off"
          >
            <option value="">Select Sales Rep</option>
            {salesOptions.map((opt) => (
              <option key={opt.email || opt.displayName} value={opt.email}>
                {opt.displayName}
              </option>
            ))}
          </Form.Select>
        )}
      </Form.Group>
      <Form.Group controlId="zones" className="mb-3">
        <Form.Label>Zones</Form.Label>
        <Form.Control
          type="text"
          name="zones"
          ref={zonesRef}
          defaultValue=""
          autoComplete="off"
        />
      </Form.Group>
      <Form.Group controlId="state" className="mb-3">
        <Form.Label>State</Form.Label>
        <Form.Control
          type="text"
          name="state"
          ref={stateRef}
          defaultValue=""
          autoComplete="off"
        />
      </Form.Group>
      <Form.Group controlId="sendDocumentsToSales" className="mb-3">
        <Form.Check
          type="checkbox"
          label="Send Documents To Sales"
          name="sendDocumentsToSales"
          ref={sendDocsRef}
          defaultChecked={false}
        />
      </Form.Group>
      <Form.Group controlId="customControlsEngineering" className="mb-3">
        <Form.Check
          type="checkbox"
          label="Custom Controls Engineering"
          name="customControlsEngineering"
          ref={customControlsRef}
          defaultChecked={false}
        />
      </Form.Group>
      <Form.Group controlId="newConstruction" className="mb-3">
        <Form.Check
          type="checkbox"
          label="New Construction"
          name="newConstruction"
          ref={newConstructionRef}
          defaultChecked={false}
        />
      </Form.Group>
      <Form.Group controlId="hasSubmittal" className="mb-3">
        <Form.Check
          type="checkbox"
          label="Has Submittal"
          name="hasSubmittal"
          ref={hasSubmittalRef}
          defaultChecked={false}
        />
      </Form.Group>
    </>
  );

  return (
    <Modal show={show} onHide={() => { onHide(); setApiStatus({}); setLogs([]); resetForm(); }} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {activeTab === "create" ? "Create New Job" : "Copy Existing Job"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {showSuccessMessage && <Alert variant="success">Job {activeTab === "create" ? "created" : "copied"} successfully!</Alert>}
        
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link 
              active={activeTab === "create"} 
              onClick={() => setActiveTab("create")}
            >
              Create New Job
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link 
              active={activeTab === "copy"} 
              onClick={() => setActiveTab("copy")}
            >
              Copy Existing Job
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane active={activeTab === "create"}>
            <Form onSubmit={handleCreateSubmit}>
              <JobFormFields />
              <Modal.Footer>
                <Button variant="secondary" onClick={() => { onHide(); setApiStatus({}); setLogs([]); resetForm(); }}>
                  Close
                </Button>
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? <><Spinner animation="border" size="sm" /> Creating...</> : "Create Job"}
                </Button>
              </Modal.Footer>
            </Form>
          </Tab.Pane>

          <Tab.Pane active={activeTab === "copy"}>
            <Form onSubmit={handleCopySubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Search Job to Copy</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Enter job number or name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                  <Button 
                    variant="outline-secondary" 
                    onClick={handleSearch}
                    disabled={isSearching}
                  >
                    {isSearching ? <Spinner animation="border" size="sm" /> : "Search"}
                  </Button>
                </InputGroup>
                <Form.Text className="text-muted">
                  You can search by job number or project name
                </Form.Text>
              </Form.Group>

              {searchResults.length > 0 && (
                <div className="mb-3">
                  <Form.Label>Search Results ({searchResults.length} found)</Form.Label>
                  <ListGroup style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {searchResults.map((job) => (
                      <ListGroup.Item 
                        key={job.ID || job.id} 
                        active={selectedJob && (selectedJob.ID === job.ID || selectedJob.id === job.id)}
                        onClick={() => handleSelectJob(job)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div>
                          <strong>Job Number:</strong> {job.JobNumber || job.jobNumber}
                        </div>
                        <div>
                          <strong>Project Name:</strong> {job.ProjectName || job.projectName}
                        </div>
                        {(job.ModelNumber || job.modelNumber) && (
                          <div>
                            <strong>Model:</strong> {job.ModelNumber || job.modelNumber}
                          </div>
                        )}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}

              {selectedJob && (
                <>
                  <Alert variant="info">
                    Creating a copy of: {selectedJob.JobNumber || selectedJob.jobNumber} - {selectedJob.ProjectName || selectedJob.projectName}
                  </Alert>
                  
                  <JobFormFields required={true} />
                </>
              )}

              <Modal.Footer>
                <Button variant="secondary" onClick={() => { onHide(); setApiStatus({}); setLogs([]); resetForm(); }}>
                  Close
                </Button>
                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={!selectedJob || loading}
                >
                  {loading ? <><Spinner animation="border" size="sm" /> Copying...</> : "Copy Job"}
                </Button>
              </Modal.Footer>
            </Form>
          </Tab.Pane>
        </Tab.Content>

        {loading && (
          <div className="text-center my-3">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        )}

        {showApiStatus && (
          <div className="mt-3">
            <h5>API Status:</h5>
            <ul className="list-unstyled">
              {Object.keys(apiStatus).map((api) => (
                <li key={api} className="mb-1">
                  {api}... {renderStatusIcon(apiStatus[api])}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-3">
          <h5>Logs:</h5>
          <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', padding: '10px', borderRadius: '4px' }}>
            <ul className="list-unstyled">
              {logs.map((log, index) => (
                <li key={index} className="mb-1">{log}</li>
              ))}
            </ul>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CreateJobModal;
